import { Link } from 'react-router-dom';
import SeoHead from '../components/SeoHead';
import ContactForm from '../components/ContactForm';
import { COMPANY } from '../data/seedData';
import { imageUrl } from '../lib/images';

export default function HomePage() {
  const heroImage = 'https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/img-1762-1-cropresize-1408x704.webp';

  return (
    <>
      <SeoHead
        title="RB Glasfiber- specialist i glasfiber"
        description="RB Glasfiber er specialist i udførsel og reparation af glasfiberopgaver. Vi leverer pumpebrønde, indstøbningsbrønde, tanke, udkørende reparation og specialløsninger."
        canonical="https://www.rb-glasfiber.dk/"
        ogImage={heroImage}
      />

      {/* 1. HERO SECTION (Scan-Plast inspired authoritative industrial hero) */}
      <section className="relative mt-[116px] bg-slate-950 text-white overflow-hidden border-b border-slate-800">
        {/* Background Image with fixed scroll and more transparent overlay */}
        <div
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat bg-fixed"
          style={{ backgroundImage: `url(${imageUrl(heroImage, 'width=1920,quality=85,format=auto')})` }}
        >
          {/* More transparent overlay color */}
          <div className="absolute inset-0 bg-slate-950/40 sm:bg-gradient-to-r sm:from-slate-950/65 sm:via-slate-950/40 sm:to-slate-950/15" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-28">
          <div className="max-w-3xl">
            {/* Tagline Badge */}
            <div className="inline-flex items-center gap-2.5 px-3.5 py-1.5 rounded-sm bg-slate-900/90 border border-slate-700/80 text-xs font-bold tracking-widest uppercase text-slate-200 mb-6 shadow-sm">
              <span className="w-2.5 h-2.5 rounded-full bg-[#1e9457] animate-pulse"></span>
              <span>Dansk glasfiber produktion & reparation</span>
            </div>

            {/* H1 Headline */}
            <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-[54px] font-black tracking-tight text-white uppercase leading-[1.08] mb-6">
              Glasfiberløsninger til industri, forsyning og specialopgaver
            </h1>

            {/* Intro text from scrape */}
            <p className="text-base sm:text-lg text-slate-300 leading-relaxed mb-8 max-w-2xl font-normal">
              RB Glasfiber er specialist i udførsel og reparation af glasfiberopgaver. Vi fremstiller bl.a. pumpebrønde, indstøbningsbrønde, tanke, containere og overdæknings-koncepter – og rykker ud til reparationer hos industri og forsyning.
            </p>

            {/* CTAs */}
            <div className="flex flex-wrap items-center gap-4">
              <a
                href="#kerneomraader"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-sm bg-[#1e9457] hover:bg-[#15703f] text-white font-bold uppercase tracking-wider text-sm shadow-md hover:shadow-lg transition-all duration-200 cursor-pointer"
              >
                <span>Se vores løsninger</span>
                <i className="fa-solid fa-arrow-down text-xs"></i>
              </a>

              <Link
                to="/kontakt"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-3.5 rounded-sm bg-white/10 hover:bg-white/20 text-white font-semibold text-sm border border-white/25 hover:border-white/40 backdrop-blur-xs transition-all duration-200 cursor-pointer"
              >
                <span>Kontakt os</span>
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>

              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="inline-flex items-center gap-2.5 px-4 py-3 rounded-sm text-slate-200 hover:text-white text-sm font-medium transition-colors whitespace-nowrap bg-slate-900/60 border border-slate-800 hover:border-slate-700"
              >
                <i className="fa-solid fa-phone text-[#1e9457]"></i>
                <span className="whitespace-nowrap">{COMPANY.phone}</span>
              </a>
            </div>
          </div>
        </div>

        {/* 2. KEY CAPABILITY STRIP (Scan-Plast style industrial benchmark banner) */}
        <div className="relative z-10 border-t border-slate-800 bg-slate-950/90 backdrop-blur-md">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5">
            <div className="grid grid-cols-1 sm:grid-cols-3 divide-y sm:divide-y-0 sm:divide-x divide-slate-800 text-slate-200 text-xs sm:text-sm">
              <div className="py-2 sm:py-0 sm:px-4 first:pl-0 flex items-center gap-3.5">
                <div className="w-9 h-9 rounded-sm bg-[#1e9457]/15 border border-[#1e9457]/30 flex items-center justify-center text-[#22c55e] flex-shrink-0">
                  <i className="fa-solid fa-truck-fast text-sm"></i>
                </div>
                <div>
                  <span className="font-bold text-white block uppercase tracking-wide">Udkørende reparation</span>
                  <span className="text-slate-400 text-xs">Akut & planlagt service i hele landet</span>
                </div>
              </div>

              <div className="py-2 sm:py-0 sm:px-4 flex items-center gap-3.5">
                <div className="w-9 h-9 rounded-sm bg-[#1e9457]/15 border border-[#1e9457]/30 flex items-center justify-center text-[#22c55e] flex-shrink-0">
                  <i className="fa-solid fa-certificate text-sm"></i>
                </div>
                <div>
                  <span className="font-bold text-white block uppercase tracking-wide">CE-godkendte emner</span>
                  <span className="text-slate-400 text-xs">Dokumenteret europæisk standard</span>
                </div>
              </div>

              <div className="py-2 sm:py-0 sm:px-4 last:pr-0 flex items-center gap-3.5">
                <div className="w-9 h-9 rounded-sm bg-[#1e9457]/15 border border-[#1e9457]/30 flex items-center justify-center text-[#22c55e] flex-shrink-0">
                  <i className="fa-solid fa-leaf text-sm"></i>
                </div>
                <div>
                  <span className="font-bold text-white block uppercase tracking-wide">Miljøbevidst produktion</span>
                  <span className="text-slate-400 text-xs">Høj kemikalieresistens & lang levetid</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 3. KERNEOMRÅDER & PRODUKTPROGRAM (Scan-Plast inspired modular industrial product cards) */}
      <section id="kerneomraader" className="py-20 lg:py-24 bg-slate-50 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-end justify-between mb-14 pb-6 border-b border-slate-200">
            <div>
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#1e9457] mb-2.5">
                <span className="w-2 h-2 rounded-full bg-[#1e9457]"></span>
                Kerneområder & Produktprogram
              </div>
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 uppercase tracking-tight">
                Vores kompetencer i glasfiber
              </h2>
            </div>
            <p className="text-slate-600 text-sm sm:text-base max-w-md mt-4 md:mt-0 leading-relaxed">
              Fra robuste serieproducerede standardprodukter til avancerede kundetilpassede emner og udkørende industriservice.
            </p>
          </div>

          {/* Cards Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-7">
            {/* Card 1: Indstøbningsbrønde */}
            <Link
              to="/rb-glasfiber/pumpebronde-amp-tanke"
              className="group bg-white rounded-sm border border-slate-200 hover:border-[#1e9457] hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1e9457]"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/indstoebningsbroend2-cropresize-500x375.webp', 'width=600,quality=85,format=auto')}
                  alt="Indstøbningsbrønde i glasfiber"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-0 left-0 bg-[#1e9457] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1">
                  Specialindsats
                </div>
              </div>
              <div className="p-6 sm:p-7 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#1e9457] transition-colors flex items-center justify-between gap-2">
                    <span>Indstøbningsbrønde</span>
                    <i className="fa-solid fa-arrow-right text-xs text-slate-400 group-hover:text-[#1e9457] group-hover:translate-x-1 transition-all"></i>
                  </h3>
                  <p className="text-slate-600 text-sm mt-3 leading-relaxed">
                    Fungerer som en indsats til gamle eller nedslidte pumpebrønde i beton. Produceres med barriere-lag i vinylester, som er et særdeles kemikalieresistent produkt.
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[#1e9457] group-hover:text-[#15703f]">
                  <span>Læs mere om indstøbningsbrønde</span>
                  <i className="fa-solid fa-chevron-right text-[10px] group-hover:translate-x-0.5 transition-transform"></i>
                </div>
              </div>
            </Link>

            {/* Card 2: Pumpebrønde */}
            <Link
              to="/rb-glasfiber/pumpebronde-amp-tanke"
              className="group bg-white rounded-sm border border-slate-200 hover:border-[#1e9457] hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1e9457]"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/rb-glasfiber-pumpebroend-glasfiber-thumb1-cropresize-550x413.webp', 'width=600,quality=85,format=auto')}
                  alt="Pumpebrønde i glasfiber"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-0 left-0 bg-[#1e9457] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1">
                  8-9 Størrelser
                </div>
              </div>
              <div className="p-6 sm:p-7 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#1e9457] transition-colors flex items-center justify-between gap-2">
                    <span>Pumpebrønde</span>
                    <i className="fa-solid fa-arrow-right text-xs text-slate-400 group-hover:text-[#1e9457] group-hover:translate-x-1 transition-all"></i>
                  </h3>
                  <p className="text-slate-600 text-sm mt-3 leading-relaxed">
                    Fremstillet i glasfiber-armeret polyester (Ø800 til Ø3000 mm). Kemikalieresistens, korrosionsbestandighed og vedligeholdelsesfri drift til regnvand og kloakvand.
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[#1e9457] group-hover:text-[#15703f]">
                  <span>Læs mere om pumpebrønde</span>
                  <i className="fa-solid fa-chevron-right text-[10px] group-hover:translate-x-0.5 transition-transform"></i>
                </div>
              </div>
            </Link>

            {/* Card 3: Tanke & Dobbeltvæggede tanke */}
            <Link
              to="/rb-glasfiber/pumpebronde-amp-tanke"
              className="group bg-white rounded-sm border border-slate-200 hover:border-[#1e9457] hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1e9457]"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/dobbelt-tank-2019-scaled-e1581424144496-cropresize-550x413.webp', 'width=600,quality=85,format=auto')}
                  alt="Dobbeltvæggede tanke"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-0 left-0 bg-[#1e9457] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1">
                  Dobbeltvægget
                </div>
              </div>
              <div className="p-6 sm:p-7 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#1e9457] transition-colors flex items-center justify-between gap-2">
                    <span>Tanke & Dobbeltvæggede tanke</span>
                    <i className="fa-solid fa-arrow-right text-xs text-slate-400 group-hover:text-[#1e9457] group-hover:translate-x-1 transition-all"></i>
                  </h3>
                  <p className="text-slate-600 text-sm mt-3 leading-relaxed">
                    Dobbeltvæggede tanke sikrer, at væske ikke løber ud i naturen. Vi fremstiller også trailer-tanke, mindre tanke og doseringstanke med vinylesterbarriere.
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[#1e9457] group-hover:text-[#15703f]">
                  <span>Læs mere om tanke</span>
                  <i className="fa-solid fa-chevron-right text-[10px] group-hover:translate-x-0.5 transition-transform"></i>
                </div>
              </div>
            </Link>

            {/* Card 4: Udkørende reparation */}
            <Link
              to="/rb-glasfiber/reparationer"
              className="group bg-white rounded-sm border border-slate-200 hover:border-[#1e9457] hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1e9457]"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/rep-faerch-plast-scaled-e1581422895121-cropresize-412x619.webp', 'width=600,quality=85,format=auto')}
                  alt="Glasfiber reparation"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-0 left-0 bg-[#1e9457] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1">
                  Udkørende Service
                </div>
              </div>
              <div className="p-6 sm:p-7 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#1e9457] transition-colors flex items-center justify-between gap-2">
                    <span>Reparationer i glasfiber</span>
                    <i className="fa-solid fa-arrow-right text-xs text-slate-400 group-hover:text-[#1e9457] group-hover:translate-x-1 transition-all"></i>
                  </h3>
                  <p className="text-slate-600 text-sm mt-3 leading-relaxed">
                    Vi reparerer bl.a. industrisiloer, procesbeholdere, havebassiner og både, og løser stort set alle former for reparationsarbejde – også ude hos kunden.
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[#1e9457] group-hover:text-[#15703f]">
                  <span>Læs om reparationsarbejde</span>
                  <i className="fa-solid fa-chevron-right text-[10px] group-hover:translate-x-0.5 transition-transform"></i>
                </div>
              </div>
            </Link>

            {/* Card 5: Overdækning */}
            <Link
              to="/rb-glasfiber/overdaekning"
              className="group bg-white rounded-sm border border-slate-200 hover:border-[#1e9457] hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1e9457]"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/oversaekning-cropresize-1408x704.webp', 'width=600,quality=85,format=auto')}
                  alt="Overdækning til renseanlæg"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-0 left-0 bg-[#1e9457] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1">
                  Roterende Koncept
                </div>
              </div>
              <div className="p-6 sm:p-7 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#1e9457] transition-colors flex items-center justify-between gap-2">
                    <span>Roterende overdækning</span>
                    <i className="fa-solid fa-arrow-right text-xs text-slate-400 group-hover:text-[#1e9457] group-hover:translate-x-1 transition-all"></i>
                  </h3>
                  <p className="text-slate-600 text-sm mt-3 leading-relaxed">
                    Skræddersyet overdækningskoncept til minimering af lugtgener ved procestanke og renseanlæg (bl.a. Søholt Renseanlæg, Silkeborg op til Ø27 m).
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[#1e9457] group-hover:text-[#15703f]">
                  <span>Læs om overdækning</span>
                  <i className="fa-solid fa-chevron-right text-[10px] group-hover:translate-x-0.5 transition-transform"></i>
                </div>
              </div>
            </Link>

            {/* Card 6: Standard Produkter */}
            <Link
              to="/produkter"
              className="group bg-white rounded-sm border border-slate-200 hover:border-[#1e9457] hover:shadow-xl transition-all duration-300 flex flex-col overflow-hidden focus:outline-none focus:ring-2 focus:ring-[#1e9457]"
            >
              <div className="relative aspect-[16/10] overflow-hidden bg-slate-100">
                <img
                  src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/joca-4-hjulet-affaldsbeholder-glasfiber-container2-cropresize-625x625.webp', 'width=600,quality=85,format=auto')}
                  alt="Standard produkter containere"
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                <div className="absolute top-0 left-0 bg-[#1e9457] text-white text-[11px] font-bold uppercase tracking-wider px-3 py-1">
                  Containere & Igloer
                </div>
              </div>
              <div className="p-6 sm:p-7 flex-1 flex flex-col justify-between space-y-4">
                <div>
                  <h3 className="text-xl font-bold text-slate-900 group-hover:text-[#1e9457] transition-colors flex items-center justify-between gap-2">
                    <span>Standard produkter</span>
                    <i className="fa-solid fa-arrow-right text-xs text-slate-400 group-hover:text-[#1e9457] group-hover:translate-x-1 transition-all"></i>
                  </h3>
                  <p className="text-slate-600 text-sm mt-3 leading-relaxed">
                    Håndoplagte 4-hjulede containere (300-1400L), Model Joca genbrugsigloer, Fancy containere, batteribokse og sand-salt containere.
                  </p>
                </div>
                <div className="pt-4 border-t border-slate-100 flex items-center justify-between text-xs font-bold uppercase tracking-wider text-[#1e9457] group-hover:text-[#15703f]">
                  <span>Se standard produkter</span>
                  <i className="fa-solid fa-chevron-right text-[10px] group-hover:translate-x-0.5 transition-transform"></i>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>

      {/* 4. HIGHLIGHT BANNER: UDKØRENDE REPARATION (Scan-Plast inspired high-contrast service banner) */}
      <section className="bg-slate-900 text-white py-16 border-b border-slate-800 relative overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(#1e9457_1px,transparent_1px)] [background-size:28px_28px] opacity-10" />
        
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-slate-950/80 border border-slate-800 rounded-sm p-8 sm:p-10 lg:p-12 shadow-2xl flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="space-y-4 max-w-2xl">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-[#1e9457]/20 border border-[#1e9457]/40 text-[#22c55e] text-xs font-bold uppercase tracking-wider">
                <i className="fa-solid fa-wrench"></i>
                <span>Udkørende reparation i hele landet</span>
              </div>
              
              <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight uppercase leading-tight">
                Akut eller planlagt glasfiberreparation ude hos dig
              </h2>
              
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                Hos RB Glasfiber udføres også forskellige former for reparationsarbejde ude hos kunden. Vi reparerer bl.a. både, siloer, havebassiner og industritanke i glasfiber. Uanset opgavens størrelse rykker vi hurtigt ud.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row lg:flex-col gap-3.5 w-full lg:w-auto flex-shrink-0">
              <a
                href={`tel:${COMPANY.phoneRaw}`}
                className="inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-sm bg-[#1e9457] hover:bg-[#15703f] text-white font-bold uppercase tracking-wider text-sm shadow-md transition-all whitespace-nowrap"
              >
                <i className="fa-solid fa-phone"></i>
                <span className="whitespace-nowrap">Ring nu: {COMPANY.phone}</span>
              </a>
              
              <Link
                to="/rb-glasfiber/reparationer"
                className="inline-flex items-center justify-center gap-2.5 px-7 py-4 rounded-sm bg-slate-800 hover:bg-slate-700 text-white font-semibold text-sm border border-slate-700 hover:border-slate-600 transition-all text-center"
              >
                <span>Læs om udkørende reparation</span>
                <i className="fa-solid fa-arrow-right text-xs"></i>
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* 5. SPECIALOPGAVER & CLASSIC CARS SECTION (Scan-Plast inspired technical portfolio showcase) */}
      <section className="py-20 lg:py-24 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-14 items-center">
            {/* Left Column: Context & Highlight */}
            <div className="lg:col-span-5 space-y-6">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-sm bg-[#1e9457]/10 border border-[#1e9457]/30 text-[#1e9457] text-xs font-bold uppercase tracking-widest">
                <i className="fa-solid fa-cubes-stacked"></i>
                <span>Specialløsninger i komposit</span>
              </div>
              
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight uppercase leading-tight">
                Specialopgaver & unikke glasfiberemner
              </h2>
              
              <p className="text-slate-600 text-sm sm:text-base leading-relaxed">
                Står du med en idé til en speciel opgave – så hjælper RB Glasfiber dig videre. Vi udfører diverse opgaver i både stål, plast, træ og glasfiber alt efter kundens ønsker.
              </p>

              {/* Exact feature list from scrape */}
              <div className="space-y-3.5 text-sm text-slate-700 pt-1">
                <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-100 rounded-sm">
                  <div className="w-5 h-5 rounded-full bg-[#1e9457]/15 text-[#1e9457] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs"></i>
                  </div>
                  <span><strong>Renault Alpine 310:</strong> Specialreparation og opbygning af glasfiberkarosseri på historiske sportsvogne.</span>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-100 rounded-sm">
                  <div className="w-5 h-5 rounded-full bg-[#1e9457]/15 text-[#1e9457] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs"></i>
                  </div>
                  <span><strong>Commercial Barrier:</strong> Terrorsikring i form af tunge glasfiberbeholdere i samarbejde med Rockwool.</span>
                </div>
                
                <div className="flex items-start gap-3 p-3 bg-slate-50 border border-slate-100 rounded-sm">
                  <div className="w-5 h-5 rounded-full bg-[#1e9457]/15 text-[#1e9457] flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="fa-solid fa-check text-xs"></i>
                  </div>
                  <span><strong>Specialemner:</strong> Mega mælkekarton til Arla, fiskekutter affaldsstation til Hvide Sande, mandskabskurve til lift og svømmehalstrapper.</span>
                </div>
              </div>

              <div className="pt-2">
                <Link
                  to="/rb-glasfiber/specialopgaver"
                  className="inline-flex items-center gap-2.5 px-7 py-3.5 rounded-sm bg-[#1e9457] hover:bg-[#15703f] text-white font-bold uppercase tracking-wider text-sm shadow-md transition-all"
                >
                  <span>Udforsk alle specialopgaver</span>
                  <i className="fa-solid fa-arrow-right text-xs"></i>
                </Link>
              </div>
            </div>

            {/* Right Column: Visual Case Showcase */}
            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 gap-4 sm:gap-5">
                <div className="relative rounded-sm overflow-hidden border border-slate-200 aspect-[4/3] bg-slate-900 group shadow-sm">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/131394-billede1-cropresize-375x281.webp', 'width=500,quality=85,format=auto')}
                    alt="Renault Alpine 310 reparation"
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent flex items-end p-4">
                    <span className="text-xs font-bold text-white uppercase tracking-wider">Rep. af Renault Alpine 310</span>
                  </div>
                </div>

                <div className="relative rounded-sm overflow-hidden border border-slate-200 aspect-[4/3] bg-slate-900 group shadow-sm">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/slide-2-cropresize-1408x704.webp', 'width=500,quality=85,format=auto')}
                    alt="Commercial Barrier terrorsikring"
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent flex items-end p-4">
                    <span className="text-xs font-bold text-white uppercase tracking-wider">Commercial Barrier terrorsikring</span>
                  </div>
                </div>

                <div className="relative rounded-sm overflow-hidden border border-slate-200 aspect-[4/3] bg-slate-900 group shadow-sm">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/lasfiber-specialopgave-glasfiber-arla-mega-maelkekaton-2-cropresize-375x563.webp', 'width=500,quality=85,format=auto')}
                    alt="Mega mælkekarton til Arla"
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent flex items-end p-4">
                    <span className="text-xs font-bold text-white uppercase tracking-wider">Mælkekarton til Arla</span>
                  </div>
                </div>

                <div className="relative rounded-sm overflow-hidden border border-slate-200 aspect-[4/3] bg-slate-900 group shadow-sm">
                  <img
                    src={imageUrl('https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/658166-trappe-ulfborg-svoemmehal-scaled-1-cropresize-500x375.webp', 'width=500,quality=85,format=auto')}
                    alt="Trappe til Ulfborg Svømmehal"
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-slate-950/85 via-slate-950/20 to-transparent flex items-end p-4">
                    <span className="text-xs font-bold text-white uppercase tracking-wider">Trappe Ulfborg Svømmehal</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 6. PRODUKTION OG KOMPETENCER (Scan-Plast inspired technical processes) */}
      <section className="py-20 lg:py-24 bg-slate-100/70 border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#1e9457] mb-2.5">
              <i className="fa-solid fa-gears"></i>
              <span>Teknisk produktion & processer</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 uppercase tracking-tight">
              Metoder og håndværk i produktionen
            </h2>
            <p className="text-slate-600 text-sm sm:text-base mt-3 leading-relaxed">
              Vi behersker de førende fremstillingsmetoder i glasfiber og komposit for at opnå den optimale kombination af styrke, finish og kemikalieresistens.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Process 01 */}
            <div className="relative p-7 rounded-sm bg-white border border-slate-200 hover:border-[#1e9457] hover:shadow-lg transition-all duration-300 flex flex-col justify-between space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-sm bg-[#1e9457]/10 text-[#1e9457] flex items-center justify-center text-xl font-bold">
                    <i className="fa-solid fa-hands"></i>
                  </div>
                  <span className="text-2xl font-black text-slate-200">01</span>
                </div>
                <h3 className="font-bold text-lg text-slate-900 pt-2">Støbning med håndoplæg</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  Klassisk håndoplagt glasfiber med nøje kontrolleret blandingsforhold i måtter, polyester og gelcoat for maksimal styrke og forstærkning.
                </p>
              </div>
            </div>

            {/* Process 02 */}
            <div className="relative p-7 rounded-sm bg-white border border-slate-200 hover:border-[#1e9457] hover:shadow-lg transition-all duration-300 flex flex-col justify-between space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-sm bg-[#1e9457]/10 text-[#1e9457] flex items-center justify-center text-xl font-bold">
                    <i className="fa-solid fa-layer-group"></i>
                  </div>
                  <span className="text-2xl font-black text-slate-200">02</span>
                </div>
                <h3 className="font-bold text-lg text-slate-900 pt-2">Foliestøbning</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  Avanceret støbemetode der sikrer jævn overflade, tæthed og reproducerbar kvalitet ved specialiserede emner og serieproduktion.
                </p>
              </div>
            </div>

            {/* Process 03 */}
            <div className="relative p-7 rounded-sm bg-white border border-slate-200 hover:border-[#1e9457] hover:shadow-lg transition-all duration-300 flex flex-col justify-between space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-sm bg-[#1e9457]/10 text-[#1e9457] flex items-center justify-center text-xl font-bold">
                    <i className="fa-solid fa-arrows-split-up-and-left"></i>
                  </div>
                  <span className="text-2xl font-black text-slate-200">03</span>
                </div>
                <h3 className="font-bold text-lg text-slate-900 pt-2">Dobbelt forme</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  Støbning i dobbeltforme giver glat og ensartet finish på både inderside og yderside, velegnet til låg, kurve og konstruktionselementer.
                </p>
              </div>
            </div>

            {/* Process 04 */}
            <div className="relative p-7 rounded-sm bg-white border border-slate-200 hover:border-[#1e9457] hover:shadow-lg transition-all duration-300 flex flex-col justify-between space-y-4">
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <div className="w-12 h-12 rounded-sm bg-[#1e9457]/10 text-[#1e9457] flex items-center justify-center text-xl font-bold">
                    <i className="fa-solid fa-shapes"></i>
                  </div>
                  <span className="text-2xl font-black text-slate-200">04</span>
                </div>
                <h3 className="font-bold text-lg text-slate-900 pt-2">Formbygning & RTM</h3>
                <p className="text-slate-600 text-xs sm:text-sm leading-relaxed">
                  Egen fremstilling af forme efter kundens ønsker og behov, samt RTM vakuumstøbning af tekniske komponenter og vinger.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 7. DIRECT CONTACT & INQUIRY SECTION (Design Reference matching split two-column on dark image overlay) */}
      <section className="relative py-20 lg:py-28 overflow-hidden bg-slate-950 text-white border-t border-slate-800">
        {/* Background image with gradient overlay for optimal text readability */}
        <div
          className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
          style={{
            backgroundImage: `url(${imageUrl(
              'https://assets.auxodev.dk/assets/9692fb57-52ae-41f1-9d47-5179b8f3a973/formbygning-1-scaled-e1582012516853-cropresize-1408x704.webp',
              'width=1920,quality=80,format=auto'
            )})`,
          }}
        >
          {/* Multi-stop gradient: heavy contrast on left where text sits, smooth transition across */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/95 via-slate-950/80 to-slate-950/70 lg:bg-gradient-to-r lg:from-slate-950/95 lg:via-slate-950/80 lg:to-slate-950/45" />
          <div className="absolute inset-0 bg-slate-950/30" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 xl:gap-20 items-start">
            {/* Left Column: Heading, Subheading, and Value Proposition */}
            <div className="lg:col-span-5 space-y-6 sm:space-y-8">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-[#1e9457]">
                <i className="fa-solid fa-comments"></i>
                <span>Direkte kontakt & rådgivning</span>
              </div>

              {/* Display heading: large, bold, sans-serif with accent highlight */}
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black uppercase tracking-tight text-white leading-[1.08]">
                Kontakt <span className="text-[#1e9457]">RB Glasfiber</span>
              </h2>

              {/* Subheading: bold and slightly larger than body text with accent highlight */}
              <p className="text-lg sm:text-xl font-bold text-slate-100 leading-snug">
                Specialiseret rådgivning og <span className="text-[#1e9457]">skræddersyede løsninger</span> i komposit og glasfiber
              </p>

              {/* Body text paragraphs: regular weight sans-serif */}
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed font-normal">
                Har du spørgsmål til et produkt, brug for et uforpligtende tilbud eller akut behov for udkørende reparation? Kontakt os i dag – vi står klar med teknisk vejledning.
              </p>

              {/* Value Proposition Highlights */}
              <div className="space-y-3 pt-2">
                <div className="flex items-start gap-3 text-sm text-slate-200">
                  <div className="w-5 h-5 rounded-full bg-[#1e9457]/20 border border-[#1e9457]/50 flex items-center justify-center text-[#22c55e] text-xs flex-shrink-0 mt-0.5">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <span>Hurtig tilbagemelding og uforpligtende prisoverslag</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-slate-200">
                  <div className="w-5 h-5 rounded-full bg-[#1e9457]/20 border border-[#1e9457]/50 flex items-center justify-center text-[#22c55e] text-xs flex-shrink-0 mt-0.5">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <span>Udkørende reparation hos industri, forsyning og landbrug</span>
                </div>
                <div className="flex items-start gap-3 text-sm text-slate-200">
                  <div className="w-5 h-5 rounded-full bg-[#1e9457]/20 border border-[#1e9457]/50 flex items-center justify-center text-[#22c55e] text-xs flex-shrink-0 mt-0.5">
                    <i className="fa-solid fa-check"></i>
                  </div>
                  <span>Egen formbygning og fremstilling af tekniske emner</span>
                </div>
              </div>

              {/* Direct Contact Cards */}
              <div className="pt-2 space-y-3 text-sm">
                <a
                  href={`tel:${COMPANY.phoneRaw}`}
                  className="flex items-center gap-4 p-4 rounded-xl border border-slate-700/80 bg-slate-900/80 hover:border-[#1e9457] hover:bg-slate-900 transition-all group"
                >
                  <div className="w-11 h-11 rounded-lg bg-[#1e9457]/15 text-[#1e9457] flex items-center justify-center text-lg flex-shrink-0 group-hover:bg-[#1e9457] group-hover:text-white transition-colors">
                    <i className="fa-solid fa-phone"></i>
                  </div>
                  <div>
                    <span className="text-xs uppercase font-bold text-slate-400 block">Telefonisk henvendelse</span>
                    <span className="font-bold text-base text-white group-hover:text-[#1e9457] transition-colors">{COMPANY.phone}</span>
                  </div>
                </a>

                <a
                  href={`mailto:${COMPANY.email}`}
                  className="flex items-center gap-4 p-4 rounded-xl border border-slate-700/80 bg-slate-900/80 hover:border-[#1e9457] hover:bg-slate-900 transition-all group"
                >
                  <div className="w-11 h-11 rounded-lg bg-[#1e9457]/15 text-[#1e9457] flex items-center justify-center text-lg flex-shrink-0 group-hover:bg-[#1e9457] group-hover:text-white transition-colors">
                    <i className="fa-solid fa-envelope"></i>
                  </div>
                  <div>
                    <span className="text-xs uppercase font-bold text-slate-400 block">Send os en e-mail</span>
                    <span className="font-bold text-base text-white group-hover:text-[#1e9457] transition-colors">{COMPANY.email}</span>
                  </div>
                </a>

                <div className="flex items-center gap-4 p-4 rounded-xl border border-slate-700/80 bg-slate-900/80">
                  <div className="w-11 h-11 rounded-lg bg-slate-800 text-slate-400 flex items-center justify-center text-lg flex-shrink-0">
                    <i className="fa-solid fa-location-dot"></i>
                  </div>
                  <div>
                    <span className="text-xs uppercase font-bold text-slate-400 block">Produktion & værksted</span>
                    <span className="font-bold text-sm text-slate-200">{COMPANY.address.full}</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Right Column: Form with multiple input fields, checkboxes, file upload button, and submit CTA */}
            <div className="lg:col-span-7">
              <ContactForm variant="reference" showCheckboxes={true} />
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
